/**
 * layout-resolver — F12.H T12.H.11 (commit 3/3).
 *
 * Frontend helper do runtime resolve hybrid layoutu per kategoria. Lustro
 * algorytmu z backendu (`/api/modules/:id/layouts/active`) — używamy lokalnie
 * bo wszystkie layouty modułu i tak są w pamięci (przez `moduleQuery`),
 * unikamy dodatkowego round-tripu.
 *
 * Algorytm kaskadowy:
 *  1. Jeśli `categoryId` podany → szukaj layoutów z `categoryId === <id>`.
 *     Z dopasowanych wybierz `isDefault`, fallback pierwszy.
 *  2. Brak match po kategorii → fallback do uniwersalnych (`categoryId === null`).
 *     Z dopasowanych wybierz `isDefault`, fallback pierwszy.
 *  3. Brak universal → fallback any `isDefault` form.
 *  4. Brak → pierwszy form layout.
 *  5. Brak form layouts → null.
 *
 * Role-based matching ZAPLANOWANE jako bonus (DB scaffolding gotowy z migracji
 * 0021), ale obecna iteracja go pomija. Dorobimy jak będzie realnie potrzeba.
 */

export interface LayoutLike {
  id: string;
  layoutType: string;
  isDefault: boolean;
  categoryId?: string | null;
  roleIds?: string[];
}

export function resolveActiveLayout<T extends LayoutLike>(
  layouts: T[],
  categoryId: string | null | undefined,
  forcedLayoutId?: string | null,
): T | null {
  return resolveActiveLayoutWithMatch(layouts, categoryId, forcedLayoutId).layout;
}

/** Źródło dopasowania — pomocne do UI (np. badge „uniwersalny" gdy nie ma dedykowanego). */
export type LayoutMatchSource =
  | 'forced'
  | 'category'
  | 'universal'
  | 'anyDefault'
  | 'first'
  | 'none';

export interface LayoutResolveResult<T> {
  layout: T | null;
  source: LayoutMatchSource;
}

/**
 * Wariant z informacją „skąd resolver wziął layout" — używane w `RecordFormPage`
 * żeby pokazać badge gdy layout to fallback (universal/any default) zamiast
 * dedykowanego per-kategoria.
 */
export function resolveActiveLayoutWithMatch<T extends LayoutLike>(
  layouts: T[],
  categoryId: string | null | undefined,
  forcedLayoutId?: string | null,
): LayoutResolveResult<T> {
  const forms = layouts.filter((l) => l.layoutType === 'form');
  if (forms.length === 0) return { layout: null, source: 'none' };

  // 0. Wymuszony layout z pozycji menu tenanta (viewRef) — ma pierwszeństwo przed
  // kaskadą per-kategoria. Gdy wskazany id nie istnieje wśród layoutów formularza,
  // spadamy do normalnej kaskady (wsteczna zgodność).
  if (forcedLayoutId) {
    const forced = forms.find((l) => l.id === forcedLayoutId);
    if (forced) return { layout: forced, source: 'forced' };
  }

  // 1. Match ścisły kategorii
  if (categoryId) {
    const matchCategory = forms.filter((l) => l.categoryId === categoryId);
    if (matchCategory.length > 0) {
      return {
        layout: matchCategory.find((l) => l.isDefault) ?? matchCategory[0] ?? null,
        source: 'category',
      };
    }
  }

  // 2. Fallback universal (category_id IS NULL)
  const universal = forms.filter((l) => l.categoryId == null);
  if (universal.length > 0) {
    return {
      layout: universal.find((l) => l.isDefault) ?? universal[0] ?? null,
      source: 'universal',
    };
  }

  // 3. Fallback any default
  const anyDefault = forms.find((l) => l.isDefault);
  if (anyDefault) return { layout: anyDefault, source: 'anyDefault' };

  // 4. Fallback first
  return { layout: forms[0] ?? null, source: 'first' };
}
